import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { css, DirectionalHint, getTheme, Icon, ISelection, ITheme, Link, Stack, StackItem, Text } from '@fluentui/react'
import { Destination } from '../../../context/AxiosContext'
import { baseListStyles } from '../BaseList'
import { EmailItem } from './EmailList'
import useIsTouchDevice from '../../../hooks/useIsTouchDevice'
import StealthLink from '../../../components/StealthLink'
import InfoTooltip from '../../../components/InfoTooltip'
import EmailLocationList from './EmailLocationList'
import FormattedDateTime from '../../Date/FormattedDateTime'
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components'
import { documentStyles } from '../Document/DocumentListItem'
import { useBoolean } from '@fluentui/react-hooks'

// @ts-expect-error("dompurify exports differ from what typescript is expecting and cannot see the default export")
import DOMPurify from 'dompurify'

type Props = {
    item?: EmailItem
    index?: number
    selection?: ISelection
    showLocation?: boolean
}

const theme: ITheme = getTheme()
const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    date: { whiteSpace: 'nowrap' },
    snippet: { color: theme.palette.neutralSecondary },
    snippetExpanded: { whiteSpace: 'pre-line' },
    growNoWrap: { flexGrow: 1, minWidth: 0 },
    selectIcon: { display: 'flex' },
})

export default function EmailListItem({ item, selection, showLocation }: Props): ReactElement {
    const styles = useStyles()
    const isSelected = useMemo(() => selection.isKeySelected(item.id), [selection.getSelection()])
    const index = useMemo(() => selection.getItemIndex(item.id), [selection])
    const isTouchDevice = useIsTouchDevice()
    const [isSelectable, setSelectable] = useState<boolean>(false)
    const [isExpanded, { toggle: toggleExpansion }] = useBoolean(false)

    return (
        <div
            className={css(styles.container, baseListStyles.itemCell, isSelected && 'is-selected')}
            data-selection-index={index}
            data-is-focusable={true}
            data-selection-toggle={true}
            onMouseEnter={() => setSelectable(true)}
            onMouseLeave={() => setSelectable(false)}
        >
            <Stack className={styles.growNoWrap}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's2' }}>
                    <Icon iconName={item.type} />
                    <Text variant="mediumPlus" nowrap className={styles.growNoWrap}>
                        <StealthLink
                            id={item.id}
                            universalId={item.internetMessageId}
                            destination={item.destination}
                            url={item.url}
                        >
                            {item?.recipient || ''}
                        </StealthLink>
                    </Text>
                    {item?.hasAttachment && <Icon iconName="Attach" />}
                    <FormattedDateTime date={item.date}></FormattedDateTime>
                </Stack>
                <Stack horizontal verticalFill verticalAlign="end" tokens={{ childrenGap: 's2' }}>
                    <StackItem className={styles.growNoWrap}>
                        <Stack horizontal tokens={{ childrenGap: 's2' }}>
                            <Text variant="medium" block nowrap>
                                {item?.subject || ''}
                            </Text>
                            {item.destination === Destination.ReplicationEmail && (
                                <Link
                                    onMouseDown={event => event.stopPropagation()}
                                    onClick={event => {
                                        toggleExpansion()
                                        event.stopPropagation()
                                    }}
                                    styles={{ root: documentStyles.icon }}
                                >
                                    <Icon iconName="RedEye" />
                                </Link>
                            )}
                        </Stack>
                        {isExpanded ? (
                            <Text
                                block
                                nowrap
                                variant="small"
                                className={mergeClasses(styles.snippet, styles.snippetExpanded)}
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(item?.snippet),
                                    }}
                                />
                            </Text>
                        ) : (
                            <Text block nowrap variant="small" className={styles.snippet}>
                                {item?.snippet || ''}
                            </Text>
                        )}
                    </StackItem>
                    <Stack tokens={{ childrenGap: 's2' }}>
                        {showLocation && item.destination === Destination.ReplicationEmail && (
                            <StackItem data-selection-toggle={false}>
                                <InfoTooltip
                                    calloutProps={{
                                        directionalHint: DirectionalHint.leftCenter,
                                        calloutMaxWidth: window.innerWidth - 80,
                                        calloutMaxHeight: window.innerHeight - 16,
                                    }}
                                >
                                    <EmailLocationList item={item} />
                                </InfoTooltip>
                            </StackItem>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <div>
                {(isSelectable || isTouchDevice || isSelected) && (
                    <Link className={styles.selectIcon} to="#">
                        <Icon iconName="Touch" />
                    </Link>
                )}
            </div>
        </div>
    )
}
